'use client'

import Image from 'next/image'
import NextLink from 'next/link'

import { getImageDimensions } from '@sanity/asset-utils'
import { stegaClean } from 'next-sanity'

import { ARTICLE_BLOCK_SPACING } from '@/app/utils/articleBlock'
import { cn } from '@/app/utils/style'
import type { ContentImage as ContentImageType } from '@/sanity.types'
import { dataAttr, urlForImage } from '@/sanity/lib/utils'

type AspectRatio = 'wide' | 'square' | 'portrait'

const aspectRatioConfig: Record<
  AspectRatio,
  {
    ratio: string
    width: number
    height: number
    classes: string
    sizes: string
  }
> = {
  wide: {
    ratio: '16/9',
    width: 1120,
    height: 630,
    classes: 'max-w-full max-h-[200px] md:max-h-[350px] lg:max-w-[1120px] lg:max-h-[630px]',
    sizes: '(max-width: 768px) 100vw, (max-width: 1024px) 768px, 1120px',
  },
  square: {
    ratio: '1/1',
    width: 560,
    height: 560,
    classes: 'max-w-full max-h-full md:max-w-full md:max-h-full lg:max-w-[560px] lg:max-h-[560px]',
    sizes: '(max-width: 768px) 100vw, 560px',
  },
  portrait: {
    ratio: '3/4',
    width: 560,
    height: 747,
    classes: 'max-w-full max-h-full md:max-w-full md:max-h-full lg:max-w-[560px] lg:max-h-[747px]',
    sizes: '(max-width: 768px) 100vw, 560px',
  },
}

type ImageFit = 'cover' | 'contain'

const EXTERNAL_LINK = /^https?:\/\//

interface ContentImageProps {
  value: ContentImageType & { _key?: string }
  // 'contain' shows the whole image inside the aspect-ratio box instead of cropping it
  fit?: ImageFit
  documentId?: string
  documentType?: string
  parentPath?: string
}

export function ContentImage({
  value,
  fit = 'cover',
  documentId,
  documentType,
  parentPath,
}: ContentImageProps) {
  const { _key, image, aspectRatio: rawAspectRatio, caption, link, linkNewTab, alignment } = value

  if (!image?.asset) {
    return null
  }

  const aspectRatio = (stegaClean(rawAspectRatio) as AspectRatio) || 'wide'
  const config = aspectRatioConfig[aspectRatio]

  const imageUrl = urlForImage(image)?.url()

  if (!imageUrl) {
    return null
  }

  // Create data attribute for presentation mode if we have all required context
  const attr =
    _key && documentId && documentType && parentPath
      ? dataAttr({
          id: documentId,
          type: documentType,
          path: `${parentPath}[_key=="${_key}"]`,
        })
      : undefined

  const cleanLink = stegaClean(link)
  const floatSide =
    stegaClean(alignment) === 'left' || stegaClean(alignment) === 'right'
      ? stegaClean(alignment)
      : null
  const newTab = stegaClean(linkNewTab) === true
  const linkTarget = newTab
    ? ({ target: '_blank', rel: 'noopener noreferrer' } as const)
    : { rel: 'noopener noreferrer' }
  const isContained = fit === 'contain'

  // Contained images use their natural ratio so the box hugs the image instead of letterboxing it
  const naturalSize = isContained ? getImageDimensions(image.asset._ref) : null
  const boxRatio = naturalSize ? `${naturalSize.width}/${naturalSize.height}` : config.ratio

  const picture = (
    <div
      className={cn('relative mx-auto overflow-hidden', naturalSize ? 'w-full' : config.classes)}
      style={{ aspectRatio: boxRatio, ...(naturalSize && { maxWidth: naturalSize.width }) }}
    >
      <Image
        loader={({ width }) => {
          const builder = urlForImage(image)?.width(width)
          if (isContained) return builder?.fit('max').url() || imageUrl
          // Generate responsive image URLs based on requested width, maintaining the aspect ratio
          const height = Math.round(width / (config.width / config.height))
          return builder?.height(height).fit('crop').url() || imageUrl
        }}
        src={imageUrl}
        alt={stegaClean(image.alt) || ''}
        width={naturalSize?.width ?? config.width}
        height={naturalSize?.height ?? config.height}
        className={cn(
          'w-full h-full rounded-sm lg:rounded-md',
          isContained ? 'object-contain' : 'object-cover',
        )}
        sizes={naturalSize ? '(max-width: 768px) 100vw, 800px' : config.sizes}
        loading="lazy"
      />
    </div>
  )

  return (
    <figure
      className={cn(
        ARTICLE_BLOCK_SPACING,
        // Floated images sit beside the text from 768px up (300px wide, like the WordPress medium size)
        floatSide === 'left' &&
          'md:float-left md:mt-0 md:mr-4xl md:mb-lg md:w-[300px] lg:mt-0 lg:mb-lg',
        floatSide === 'right' &&
          'md:float-right md:mt-0 md:mb-lg md:ml-4xl md:w-[300px] lg:mt-0 lg:mb-lg',
      )}
      {...(attr && { 'data-sanity': attr.toString() })}
    >
      {cleanLink ? (
        EXTERNAL_LINK.test(cleanLink) ? (
          <a href={cleanLink} {...linkTarget} className="block">
            {picture}
          </a>
        ) : (
          <NextLink href={cleanLink} {...(newTab && linkTarget)} className="block">
            {picture}
          </NextLink>
        )
      ) : (
        picture
      )}
      {caption && (
        <figcaption className="mt-md size-body-xs text-center font-medium">
          {stegaClean(caption)}
        </figcaption>
      )}
    </figure>
  )
}
